import { Bars2Icon, InformationCircleIcon, MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import {
  Balance,
  Collapsible,
  FlexList,
  FlexListItem,
  Separator,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import { ADA_METADATA } from "@sundaeswap/core";
import cx from "classnames";
import { FC, memo, useMemo } from "react";

import { ADA_TX_FEE } from "../../../constants/SundaeSwap.constants";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { getLpTokenFromTasteTest } from "../../../pages/TasteTestDetail/utils/getLpTokenFromReservedUtxo";
import { useProfileAppContext } from "../../../stores";
import { EOrderTransactionBuilderState, useOrderContext } from "../../../stores/OrderContext";
import BalanceWithAssetName from "../../BalanceWithAssetName";
import LpHoverCard from "../../LpHoverCard";
import TextLoadingPlaceholder from "../../TextLoadingPlaceholder";
import TasteTestCurrentPosition from "../TasteTestCurrentPositionDetails";

interface ITasteTestClaimingTransactionDetailsProps {
  isOpen: boolean;
}

const TasteTestClaimingTransactionDetails: FC<ITasteTestClaimingTransactionDetailsProps> = ({
  isOpen,
}) => {
  const { currentLocale, t } = useI18N("taste-test");
  const {
    state: {
      derived: { transaction },
    },
  } = useOrderContext();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const {
    derived: { latestHistoryRecord },
    state: { reservedUtxos, tasteTest },
  } = useTasteTestContext();
  const isBuildingTx = transaction?.state === EOrderTransactionBuilderState.BUILD;
  const isTxAvailable = transaction?.completeTx;
  const refundableDeposit = useMemo(() => {
    return new AssetAmount(latestHistoryRecord?.minUTXOCumulative?.[0].amount, ADA_METADATA);
  }, [latestHistoryRecord?.minUTXOCumulative?.[0].amount]);

  /**
   * This is the amount of ADA the user will receive.
   */
  const totalAda = useMemo(() => {
    if (isTxAvailable && transaction?.builtTx) {
      return refundableDeposit.sub(transaction.builtTx.fees.cardanoTxFee ?? ADA_TX_FEE);
    }
  }, [refundableDeposit, isTxAvailable, transaction?.builtTx?.fees?.cardanoTxFee]);

  /**
   * This is the amount of LP Tokens the user will receive.
   */
  const lpAssetAmount = getLpTokenFromTasteTest(tasteTest, reservedUtxos);

  /* -------------------------------------------------------------------------------------------------
   * ADA Transaction Fee, Deposit and LP Amount
   * -----------------------------------------------------------------------------------------------*/
  return (
    <Collapsible
      open={isOpen}
      trigger={
        <FlexList>
          <Text className="inline-block" tag="p" size="sm" weight="medium">
            {t("youWillReceive", { ns: "common" })}:
          </Text>
          {/* -------------------------------------------------------------------------------------------------
           * Deposit
           * -----------------------------------------------------------------------------------------------*/}
          <FlexListItem
            field={{
              label: (
                <div className="relative flex items-center gap-1">
                  <PlusIcon className={cx("h-3 w-3 opacity-75", toolkitCx.layout.text.success)} />
                  <Text className="flex items-center gap-1" size="sm" tag="span" variant="muted">
                    {t("detail.widget.refundableDeposit")}
                    <Tooltip
                      tooltipLabel={t("detail.widget.refundableDeposit", {
                        context: "tooltip",
                      })}
                      trigger={
                        <div>
                          <InformationCircleIcon
                            className={cx("h-4 w-4 cursor-help", toolkitCx.layout.text.muted)}
                          />
                        </div>
                      }
                    />
                  </Text>
                </div>
              ),
              variant: "muted",
            }}
            value={{
              label: isBuildingTx ? (
                <TextLoadingPlaceholder />
              ) : (
                <BalanceWithAssetName
                  abbreviate={false}
                  asset={ADA_METADATA}
                  locale={currentLocale}
                  showSymbol
                  size="sm"
                  value={refundableDeposit}
                />
              ),
            }}
          />
          {/* -------------------------------------------------------------------------------------------------
           * ADA Transaction Fee
           * -----------------------------------------------------------------------------------------------*/}
          <FlexListItem
            field={{
              label: (
                <div className="relative flex items-center gap-1">
                  <MinusIcon className={cx("h-3 w-3 opacity-75", toolkitCx.layout.text.error)} />
                  <Text size="sm" tag="span" variant="muted">
                    {t("fee", { context: "adaTx", ns: "fees" })}
                  </Text>
                </div>
              ),
              variant: "muted",
            }}
            tooltip={t("fee", {
              context: transaction?.completeTx ? "adaTxExplained" : "adaTxExplainedNextStep",
              ns: "fees",
            })}
            value={{
              label: isBuildingTx ? (
                <TextLoadingPlaceholder />
              ) : (
                <div className="inline-flex gap-[2px]">
                  {!isTxAvailable && (
                    <Text tag="span" size="sm">
                      ~{" "}
                    </Text>
                  )}
                  <Balance
                    abbreviate={false}
                    locale={currentLocale}
                    privacy={privacyEnabled}
                    showSymbol
                    size="sm"
                    value={isTxAvailable ? transaction?.builtTx?.fees?.cardanoTxFee : ADA_TX_FEE}
                  />
                </div>
              ),
            }}
          />

          <Separator orientation="horizontal" className={toolkitCx.layout.background.separator} />
          <FlexListItem
            className="!items-start"
            field={{
              label: (
                <div className="relative flex items-center gap-1">
                  <Bars2Icon className={cx("h-3 w-3 opacity-75", toolkitCx.layout.text.primary)} />
                  <Text size="sm" tag="span" variant="muted">
                    {t("totalAmount", { ns: "common" })}
                  </Text>
                </div>
              ),
              variant: "muted",
            }}
            value={{
              label: (
                <div className="flex flex-col items-end gap-1">
                  {isBuildingTx ? (
                    <>
                      <TextLoadingPlaceholder />
                      <TextLoadingPlaceholder />
                    </>
                  ) : (
                    <>
                      <Balance
                        abbreviate={false}
                        locale={currentLocale}
                        privacy={privacyEnabled}
                        showSymbol
                        size="sm"
                        value={totalAda}
                      />
                      {/** TODO: Make similar to TVLHoverCard */}
                      <div className="flex items-center gap-1">
                        <LpHoverCard lpAsset={lpAssetAmount}>
                          <Balance
                            abbreviate
                            locale={currentLocale}
                            privacy={privacyEnabled}
                            showSymbol={false}
                            size="sm"
                            value={lpAssetAmount}
                          />
                          <Text tag="span" size="sm">
                            ADA-{tasteTest?.asset.ticker}{" "}
                            {t("lpToken", { ns: "glossary", count: 2 })}
                          </Text>
                        </LpHoverCard>
                      </div>
                    </>
                  )}
                </div>
              ),
            }}
          />
        </FlexList>
      }
    >
      {/* -------------------------------------------------------------------------------------------------
       * Current Position Details
       * -----------------------------------------------------------------------------------------------*/}
      <div className={toolkitCx.layout.spacing.top.sm}>
        <TasteTestCurrentPosition isClaiming />
      </div>
    </Collapsible>
  );
};

export default memo(TasteTestClaimingTransactionDetails);
